import React from "react";
import { OVERFLOW_HYSTERESIS_PX } from "./settingsListActionColumn";

const CARD_VIEW_CONTAINER_WIDTH_PX = 480;

type OverflowMode = "full" | "icon" | "card";

function resolveAvailableWidth(wrapper: HTMLElement): number {
  // Ziel: die tatsächliche Inhalts-Fläche (im Card-Body, OHNE Card-Padding) – modus-unabhängig.
  // Wichtig: In Tabellen-Ansicht existiert .settings-list-table-wrap (Breite = exakt Inhalts-Fläche),
  // in Kartenansicht NICHT. Ohne Korrektur misst der Fallback dann die cardBody-clientWidth INKL.
  // Padding (z. B. 48px breiter). Ergebnis wäre eine modus-abhängige availableWidth → die Schwelle
  // oszilliert beim Übergang Tabelle↔Karte → Flackern. Daher in beiden Fällen denselben Inhalts-Wert
  // liefern, indem im Fallback die horizontalen Card-Paddings abgezogen werden.
  const innerTableWrap = wrapper.querySelector(".settings-list-table-wrap");
  if (innerTableWrap instanceof HTMLElement && innerTableWrap.clientWidth > 0) return innerTableWrap.clientWidth;

  const cardBody = wrapper.closest(".ck-card-body") ?? wrapper.querySelector(".ck-card-body");
  if (cardBody instanceof HTMLElement && cardBody.clientWidth > 0) {
    const cs = (typeof window !== "undefined") ? window.getComputedStyle(cardBody) : null;
    const padLeft = cs ? parseFloat(cs.paddingLeft) || 0 : 0;
    const padRight = cs ? parseFloat(cs.paddingRight) || 0 : 0;
    return Math.max(0, cardBody.clientWidth - padLeft - padRight);
  }

  const candidates: number[] = [];
  const wrapperRect = wrapper.getBoundingClientRect();
  if (wrapper.clientWidth > 0) candidates.push(wrapper.clientWidth);
  if (wrapper.parentElement && wrapper.parentElement.clientWidth > 0) candidates.push(wrapper.parentElement.clientWidth);

  if (typeof window !== "undefined") {
    const viewportRight = Math.min(window.innerWidth || wrapperRect.right, document.documentElement.clientWidth || wrapperRect.right);
    const viewportWidthFromWrapper = Math.floor(viewportRight - wrapperRect.left);
    if (viewportWidthFromWrapper > 0) candidates.push(viewportWidthFromWrapper);
  }

  return candidates.length > 0 ? Math.min(...candidates) : wrapper.clientWidth;
}

function nextModeForWidth(
  currentMode: OverflowMode,
  width: number,
  actionsEnabled: boolean,
  fullMinWidth: number,
  iconMinWidth: number,
  cardMinWidth: number,
  isNarrowViewport: boolean
): OverflowMode {
  if (width <= 0) return currentMode;
  if (isNarrowViewport) return "card";
  // Kartenansicht greift, sobald die Spaltentitel + Icon-Aktionen + Strukturspalten nicht mehr passen
  // (iconMinWidth wird header-basiert berechnet → Header sind das harte Limit; sie dürfen nie abgeschnitten
  // werden). Auf sehr schmalen Mobile-Viewports unter cardMinWidth wird ohnehin direkt Kartenansicht
  // gezeigt. Im Tabellen-Modus entscheidet fullMinWidth, ob Aktionen mit Text oder nur als Icons gezeigt
  // werden – beide Modi nutzen Ellipsis für überlange Zelleninhalte (siehe baseColumns).
  if (width < cardMinWidth) return "card";

  if (!actionsEnabled) {
    if (currentMode === "card") return fullMinWidth + OVERFLOW_HYSTERESIS_PX <= width ? "full" : "card";
    return fullMinWidth <= width ? "full" : "card";
  }

  if (currentMode === "card") {
    if (iconMinWidth + OVERFLOW_HYSTERESIS_PX > width) return "card";
    return fullMinWidth + OVERFLOW_HYSTERESIS_PX <= width ? "full" : "icon";
  }

  if (currentMode === "icon") {
    if (iconMinWidth > width) return "card";
    return fullMinWidth + OVERFLOW_HYSTERESIS_PX <= width ? "full" : "icon";
  }

  if (fullMinWidth <= width) return "full";
  return iconMinWidth <= width ? "icon" : "card";
}

/**
 * Deterministische Layout-Umschaltung für SettingsListPage.
 *
 * Die Entscheidung basiert nicht auf laufenden DOM-Overflow-Messungen, sondern auf berechneten
 * Mindestbreiten: sichtbare Header-Mindestbreiten + feste Action-Breite je Modus.
 */
export function useSettingsListTableOverflowMeasure(options: {
  wrapperRef: React.RefObject<HTMLElement | null>;
  actionsEnabled: boolean;
  fullMinWidth: number;
  iconMinWidth: number;
  columnLayoutKey: string;
  dataDependency: unknown;
  isNarrowViewport: boolean;
  cardMinWidth?: number;
}): {
  useIconOnlyInTable: boolean;
  hasSevereOverflow: boolean;
  useCardView: boolean;
  /** Gemessene Inhaltsfläche (für exakte Spaltenbreiten-Berechnung). 0 vor erster Messung. */
  availableWidth: number;
} {
  const {
    wrapperRef,
    actionsEnabled,
    fullMinWidth,
    iconMinWidth,
    columnLayoutKey,
    dataDependency,
    isNarrowViewport,
    cardMinWidth = CARD_VIEW_CONTAINER_WIDTH_PX,
  } = options;

  const [availableWidth, setAvailableWidth] = React.useState(0);
  const [mode, setMode] = React.useState<OverflowMode>(() => (isNarrowViewport ? "card" : "full"));
  const modeRef = React.useRef<OverflowMode>(isNarrowViewport ? "card" : "full");

  React.useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const measureWidth = React.useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const width = resolveAvailableWidth(wrapper);
    if (width <= 0) return;
    setAvailableWidth((prev) => (Math.abs(prev - width) < 1 ? prev : width));
  }, [wrapperRef]);

  React.useLayoutEffect(() => {
    measureWidth();
  }, [measureWidth, columnLayoutKey, dataDependency, fullMinWidth, iconMinWidth]);

  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const observer = new ResizeObserver(() => measureWidth());
    observer.observe(wrapper);
    const cardBody = wrapper.closest(".ck-card-body");
    if (cardBody instanceof HTMLElement) observer.observe(cardBody);
    return () => observer.disconnect();
  }, [measureWidth, wrapperRef]);

  React.useLayoutEffect(() => {
    const next = nextModeForWidth(
      modeRef.current,
      availableWidth,
      actionsEnabled,
      fullMinWidth,
      iconMinWidth,
      cardMinWidth,
      isNarrowViewport
    );
    setMode((prev) => (prev === next ? prev : next));
  }, [actionsEnabled, availableWidth, cardMinWidth, fullMinWidth, iconMinWidth, isNarrowViewport]);

  const useCardView = mode === "card";
  const useIconOnlyInTable = actionsEnabled && !useCardView && mode === "icon";

  return {
    useIconOnlyInTable,
    hasSevereOverflow: useCardView,
    useCardView,
    availableWidth,
  };
}
